<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1>
          <font-awesome-icon icon="bullseye" />
          {{ $t('setupKPI') }}
        </h1>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <!-- Trước là ô chữ tự do "mm/yyyy": gõ sai định dạng thì lặng lẽ trả về toàn bộ danh sách -->
            <date-picker v-model="filterFormModel.keyword" type="month" value-type="MM/YYYY" format="MM/YYYY"
              :lang="$i18n.locale" :placeholder="$t('selectMonth')" class="mr-1 mb-1"
              @change="load"></date-picker>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="c-sorting"
          v-if="['owner', 'admin', 'operationsManager', 'account', 'tech'].includes($store.state.user.role)">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button @click="create" class="btn btn-sm btn-success">
                  <font-awesome-icon icon="plus" />&nbsp;{{ $t('create') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">
            {{ $t('found', [itemCount]) }}
          </div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize"
                @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-2 text-center">{{ $t('monthKPI') }}</div>
            <div class="col-lg-3">{{ $t('kpiStatus') }}</div>
            <div class="col-lg-3">{{ $t('lastChange') }}</div>
            <div class="col-lg-4"></div>
          </div>
          <!-- Mỗi kỳ gói gọn 1 dòng: trước đây 4 dòng chỉ để hiện người tạo/người sửa nên nhìn
               cả danh sách rất dài. Chi tiết ngày tạo đưa vào tooltip. -->
          <div class="row align-items-center" v-for="item in items" :key="item._id">
            <div class="col-lg-2 text-center">
              <b>{{ !(item.month > 9) ? '0' + item.month : item.month }}/{{ item.year }}</b>
            </div>
            <div class="col-lg-3">
              <!-- bọc thêm 1 lớp: cột của c-smart-table là flex nên badge trần bị kéo giãn hết chiều ngang -->
              <div>
                <span v-if="item.itemCountBD" class="badge badge-success">
                  {{ $t('kpiItemCount', [item.itemCountBD]) }}
                </span>
                <span v-else class="badge badge-danger">
                  <font-awesome-icon icon="exclamation-triangle" />&nbsp;{{ $t('kpiNoItem') }}
                </span>
              </div>
            </div>
            <div class="col-lg-3 small text-muted">
              <span v-b-tooltip.hover.top="$t('createdBy') + ': ' + item.createdBy.name + ' — ' + $options.filters.moment(item.createTime, 'DD/MM/YYYY HH:mm')">
                {{ (item.updatedBy || item.createdBy).name }},
                {{ (item.updateTime || item.createTime) | moment('DD/MM/YYYY HH:mm') }}
              </span>
            </div>
            <div class="col-lg-4">
              <div class="c-smart-table__content">
                <div class="c-buttons clearfix is-2">
                  <div class="c-buttons__col">
                    <button @click="copy(item)" type="button"
                      v-if="['owner', 'admin', 'operationsManager', 'account', 'tech'].includes($store.state.user.role)"
                      class="btn btn-sm btn-warning mr-1">
                      <font-awesome-icon icon="copy" />&nbsp;{{ $t('copyToMonth') }}
                    </button>
                    <kpi-list type="bd" :kpi="item" @change="load"></kpi-list>
                    <button @click="deletePeriod(item)" type="button"
                      v-if="['owner', 'admin'].includes($store.state.user.role)" class="btn btn-sm btn-danger mr-1">
                      <font-awesome-icon icon="trash" />&nbsp;{{ $t('delete') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-danger" v-if="!items.length">
        <span v-if="filterFormModel.page === 1 && !filterFormModel.keyword">{{ $t('notFoundCreate') }}</span>
        <span v-if="filterFormModel.page > 1 || filterFormModel.keyword">{{ $t('notFoundSearch') }}</span>
      </div>

      <div class="col-12" v-if="itemCount">
        <div class="c-paging__count" v-if="itemCount">
          {{ $t('found', [itemCount]) }}
        </div>
        <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
          v-model="filterFormModel.page" :total-rows="itemCount" :per-page="filterFormModel.pageSize" @input="load">
        </b-pagination>
      </div>
    </div>
    <b-modal v-model="showForm" :title="formTitle" @ok="save" no-close-on-backdrop no-enforce-focus>
      <form-input :label="$t('month')" :model="formModel" attribute="month" :errors="formErrors"></form-input>
      <form-input :label="$t('year')" :model="formModel" attribute="year" :errors="formErrors"></form-input>
    </b-modal>
    <b-modal v-model="showCopyForm" :title="$t('copyKPI')" @ok="saveCopy" no-close-on-backdrop no-enforce-focus>
      <form-input :label="$t('month')" :model="copyFormModel" attribute="month" :errors="copyFormErrors"></form-input>
      <form-input :label="$t('year')" :model="copyFormModel" attribute="year" :errors="copyFormErrors"></form-input>
    </b-modal>
  </main>
</template>

<script>
import List from "@/mixins/List";
import CRUD from "@/mixins/CRUD";
import KPIList from "@/views/account/KPIList";
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';

export default {
  components: {
    'kpi-list': KPIList,
    DatePicker
  },
  mixins: [List, CRUD],
  data: function () {
    return {
      autoLoad: true,
      service: '/admin-user-kpi',
      defaultFormModel: {
        type: "other",
        keyword: null
      },
      showCopyForm: false,
      copyFormModel: {},
      copyFormErrors: [],
      copySourceId: null
    }
  },
  methods: {
    // KHÔNG đặt tên del(): mixin CRUD đã có del(id), trùng tên mà khác tham số
    deletePeriod: function (item) {
      // Kỳ đã có chỉ tiêu thì xoá là mất hết số của cả kỳ -> nói rõ trước khi hỏi
      // backend xoá toàn bộ chỉ tiêu của kỳ (cả bản ghi AM cũ) -> cảnh báo phải đếm đủ
      const total = (item.itemCountBD || 0) + (item.itemCountAM || 0);
      const warn = total
        ? this.$t('deleteKpiWarnHasItems', [this.periodLabel(item), total])
        : this.$t('deleteKpiWarn', [this.periodLabel(item)]);
      this.$swal({
        title: this.$t('deleteConfirm'),
        text: warn,
        icon: 'warning',
        showCancelButton: true,
      }).then(result => {
        if (!result.value) return;
        let loader = this.$loading.show();
        this.$service.delete(this.service + '/' + item._id).then(() => {
          this.$swal({text: this.$t('deleteSuccess'), icon: 'success', toast: true, position: 'top-end', timer: 4000});
          this.load();
        }).catch(error => {
          let data = error.response ? error.response.data : null;
          this.$swal({text: (data && data.message) || this.$t('somethingWentWrong'), icon: 'error'});
        }).finally(() => loader.hide());
      });
    },
    periodLabel: function (item) {
      return (item.month > 9 ? item.month : '0' + item.month) + '/' + item.year;
    },
    copy: function (item) {
      this.copySourceId = item._id;
      this.copyFormModel = {};
      this.copyFormErrors = [];
      this.showCopyForm = true;
    },
    saveCopy: function (evt) {
      if (evt) {
        evt.preventDefault();
      }
      let loader = this.$loading.show();
      this.$service.post(this.service + '/' + this.copySourceId + '/copy', this.copyFormModel).then(response => {
        this.showCopyForm = false;
        if (this.load) {
          this.load();
        }
        if (response.data) {
          this.$swal({
            title: this.$t('success'),
            text: this.$t('copied'),
            icon: 'success'
          });
        } else {
          this.$swal({
            title: this.$t('error'),
            text: this.$t('createFailed'),
            icon: 'error'
          });
        }
      }).catch(error => {
        if (error.response.data.message) {
          this.$swal({
            text: error.response.data.message,
            icon: 'error'
          });
          this.showCopyForm = false;
        } else {
          this.copyFormErrors = error.response.data;
        }
      }).finally(() => {
        loader.hide();
      });
    }
  },
  i18n: {
    messages: {
      en: {
        setupKPI: "Setup KPI",
        kpiItemCount: "KPI set for {0} staff",
        kpiStatus: "Status",
        lastChange: "Last change",
        kpiNoItem: "No staff assigned yet",
        copyToMonth: "Copy to another month",
        selectMonth: "Select month",
        deleteConfirm: "Delete this KPI period?",
        deleteKpiWarn: "KPI period {0} will be removed.",
        deleteKpiWarnHasItems: "KPI period {0} already has targets for {1} staff. Deleting removes the period and all of those targets.",
        deleteSuccess: "Deleted successfully",
        somethingWentWrong: "Something went wrong, please try again",
        copy: "Copy",
        copyKPI: "Copy KPI",
        copied: "Copied successfully",
        month: "Month",
        year: "Year",
        createdBy: "Created by",
        createdAt: "Created at",
        updatedBy: "Updated by",
        updatedAt: "Updated at",
        monthKPI: "Monthly KPI",
      },
      vi: {
        setupKPI: "Cài đặt KPI",
        kpiItemCount: "Đã đặt KPI cho {0} nhân viên",
        kpiStatus: "Tình trạng",
        lastChange: "Thay đổi gần nhất",
        kpiNoItem: "Chưa đặt cho nhân viên nào",
        copyToMonth: "Sao chép sang tháng khác",
        selectMonth: "Chọn tháng",
        deleteConfirm: "Xoá kỳ KPI này?",
        deleteKpiWarn: "Kỳ KPI {0} sẽ bị xoá.",
        deleteKpiWarnHasItems: "Kỳ KPI {0} đang có chỉ tiêu của {1} nhân viên. Xoá kỳ sẽ mất luôn toàn bộ chỉ tiêu đó.",
        deleteSuccess: "Xoá thành công",
        somethingWentWrong: "Có lỗi xảy ra, vui lòng thử lại",
        copy: "Sao chép",
        copyKPI: "Sao chép KPI",
        copied: "Sao chép thành công",
        month: "Tháng",
        year: "Năm",
        createdBy: "Tạo bởi",
        createdAt: "Tạo lúc",
        updatedBy: "Cập nhật bởi",
        updatedAt: "Cập nhật lúc",
        monthKPI: "KPI tháng",
      }
    }
  }
};
</script>